import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import {
  Users,
  Phone,
  Clock,
  Smile,
  TrendingUp,
  TrendingDown,
  CalendarIcon,
  Award,
  Target,
  AlertTriangle,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
} from "recharts";

const TeamsPage = () => {
  const { authAxios } = useAuth();
  const [loading, setLoading] = useState(true);
  const [agentStats, setAgentStats] = useState([]);
  const [campaignStats, setCampaignStats] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [dateRange, setDateRange] = useState({
    from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
    to: new Date(),
  });

  const fetchStats = async () => {
    try {
      const dateFrom = format(dateRange.from, "yyyy-MM-dd 00:00:00");
      const dateTo = format(dateRange.to, "yyyy-MM-dd 23:59:59");

      const [callsRes, agentsRes, campaignsRes] = await Promise.all([
        authAxios.get(`/calls?date_from=${dateFrom}&date_to=${dateTo}&limit=1000`),
        authAxios.get("/agents"),
        authAxios.get("/campaigns"),
      ]);

      const calls = callsRes.data.calls;

      // Calculate agent stats
      const agentMap = {};
      calls.forEach(call => {
        const agent = call.agent || "Sin agente";
        if (!agentMap[agent]) {
          agentMap[agent] = {
            name: agent,
            totalCalls: 0,
            analyzedCalls: 0,
            totalDuration: 0,
            sentimentSum: 0,
            sentimentCount: 0,
            riskCalls: 0,
          };
        }
        agentMap[agent].totalCalls++;
        agentMap[agent].totalDuration += call.duration || 0;
        if (call.is_analyzed) {
          agentMap[agent].analyzedCalls++;
          if (call.sentiment_analysis?.general_sentiment) {
            agentMap[agent].sentimentSum += call.sentiment_analysis.general_sentiment;
            agentMap[agent].sentimentCount++;
          }
          if (call.risk_score >= 50) {
            agentMap[agent].riskCalls++;
          }
        }
      });

      const agentStatsArray = Object.values(agentMap).map(agent => ({
        ...agent,
        avgSentiment: agent.sentimentCount > 0 
          ? (agent.sentimentSum / agent.sentimentCount).toFixed(1) 
          : "N/A",
        avgDuration: agent.totalCalls > 0 
          ? Math.round(agent.totalDuration / agent.totalCalls / 60) 
          : 0,
        riskPercentage: agent.analyzedCalls > 0 
          ? ((agent.riskCalls / agent.analyzedCalls) * 100).toFixed(1) 
          : 0,
      })).sort((a, b) => b.totalCalls - a.totalCalls);

      setAgentStats(agentStatsArray);

      // Calculate campaign stats
      const campaignMap = {};
      calls.forEach(call => {
        const campaign = call.campaign || "Sin campaña";
        if (!campaignMap[campaign]) {
          campaignMap[campaign] = {
            name: campaign,
            totalCalls: 0,
            analyzedCalls: 0,
            totalDuration: 0,
            sentimentSum: 0,
            sentimentCount: 0,
          };
        }
        campaignMap[campaign].totalCalls++;
        campaignMap[campaign].totalDuration += call.duration || 0;
        if (call.is_analyzed && call.sentiment_analysis?.general_sentiment) {
          campaignMap[campaign].analyzedCalls++;
          campaignMap[campaign].sentimentSum += call.sentiment_analysis.general_sentiment;
          campaignMap[campaign].sentimentCount++;
        }
      });

      const campaignStatsArray = Object.values(campaignMap).map(camp => ({
        ...camp,
        avgSentiment: camp.sentimentCount > 0 
          ? parseFloat((camp.sentimentSum / camp.sentimentCount).toFixed(1))
          : 5,
      })).sort((a, b) => b.totalCalls - a.totalCalls);

      setCampaignStats(campaignStatsArray);

    } catch (error) {
      console.error("Error fetching stats:", error);
      toast.error("Error al cargar estadísticas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [dateRange]);

  const getSentimentColor = (sentiment) => {
    if (sentiment === "N/A") return "bg-slate-100 text-slate-600";
    const val = parseFloat(sentiment);
    if (val >= 7) return "bg-green-100 text-green-700";
    if (val >= 5) return "bg-yellow-100 text-yellow-700";
    return "bg-red-100 text-red-700";
  };

  const topPerformers = agentStats
    .filter(a => a.avgSentiment !== "N/A")
    .sort((a, b) => parseFloat(b.avgSentiment) - parseFloat(a.avgSentiment))
    .slice(0, 5);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Equipos y Agentes</h1>
          <p className="text-slate-500 mt-1">Métricas de rendimiento por agente y campaña</p>
        </div>

        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="flex items-center gap-2" data-testid="teams-date-filter">
              <CalendarIcon className="w-4 h-4" />
              {format(dateRange.from, "dd MMM", { locale: es })} - {format(dateRange.to, "dd MMM", { locale: es })}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end">
            <Calendar
              mode="range"
              selected={dateRange}
              onSelect={(range) => range && setDateRange(range)}
              numberOfMonths={2}
              locale={es}
            />
          </PopoverContent>
        </Popover>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-sky-50 border-sky-100">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 bg-sky-100 rounded-xl flex items-center justify-center">
              <Users className="w-6 h-6 text-sky-600" />
            </div>
            <div>
              <p className="text-3xl font-bold text-sky-700">{agentStats.length}</p>
              <p className="text-sm text-sky-600">Agentes Activos</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-green-50 border-green-100">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <Phone className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-3xl font-bold text-green-700">
                {agentStats.reduce((sum, a) => sum + a.totalCalls, 0)}
              </p>
              <p className="text-sm text-green-600">Llamadas Totales</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-orange-50 border-orange-100">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
              <Target className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <p className="text-3xl font-bold text-orange-700">{campaignStats.length}</p>
              <p className="text-sm text-orange-600">Campañas</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-purple-50 border-purple-100">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
              <Award className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="text-3xl font-bold text-purple-700">
                {topPerformers[0]?.avgSentiment || "N/A"}
              </p>
              <p className="text-sm text-purple-600">Mejor Sentimiento</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Campaign Performance */}
        <Card data-testid="campaign-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold">Rendimiento por Campaña</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={campaignStats.slice(0, 8)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis type="number" tick={{ fontSize: 12, fill: '#64748B' }} />
                  <YAxis 
                    dataKey="name" 
                    type="category" 
                    tick={{ fontSize: 11, fill: '#64748B' }}
                    width={100}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: '1px solid #E2E8F0',
                      borderRadius: '8px'
                    }}
                  />
                  <Bar dataKey="totalCalls" fill="#0EA5E9" name="Llamadas" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Top Performers */}
        <Card data-testid="top-performers">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <Award className="w-5 h-5 text-orange-500" />
              Top Agentes por Sentimiento
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {topPerformers.map((agent, index) => (
                <div key={agent.name} className="flex items-center gap-4">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
                    index === 0 ? 'bg-yellow-500' :
                    index === 1 ? 'bg-slate-400' :
                    index === 2 ? 'bg-orange-400' : 'bg-slate-300'
                  }`}>
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-slate-700">{agent.name}</span>
                      <Badge className={getSentimentColor(agent.avgSentiment)}>
                        {agent.avgSentiment}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-slate-500">
                      <span>{agent.totalCalls} llamadas</span>
                      <span>{agent.avgDuration} min prom.</span>
                    </div>
                  </div>
                </div>
              ))}
              {topPerformers.length === 0 && (
                <p className="text-center text-slate-500 py-4">No hay datos suficientes</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Agent Details Table */}
      <Card data-testid="agents-table">
        <CardHeader>
          <CardTitle className="text-lg font-semibold">Detalle por Agente</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Agente</TableHead>
                <TableHead className="text-center">Llamadas</TableHead>
                <TableHead className="text-center">Analizadas</TableHead>
                <TableHead className="text-center">Duración Prom.</TableHead>
                <TableHead className="text-center">Sentimiento</TableHead>
                <TableHead className="text-center">Riesgo</TableHead>
                <TableHead>Rendimiento</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {agentStats.map((agent) => {
                const performance = agent.avgSentiment !== "N/A" 
                  ? Math.min(100, parseFloat(agent.avgSentiment) * 10) 
                  : 0;
                return (
                  <TableRow key={agent.name} data-testid={`agent-row-${agent.name}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center">
                          <span className="text-sky-600 font-semibold">
                            {agent.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <span className="font-medium">{agent.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">{agent.totalCalls}</TableCell>
                    <TableCell className="text-center">{agent.analyzedCalls}</TableCell>
                    <TableCell className="text-center">{agent.avgDuration} min</TableCell>
                    <TableCell className="text-center">
                      <Badge className={getSentimentColor(agent.avgSentiment)}>
                        {agent.avgSentiment}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      {parseFloat(agent.riskPercentage) > 30 ? (
                        <Badge className="bg-red-100 text-red-700">
                          <AlertTriangle className="w-3 h-3 mr-1" />
                          {agent.riskPercentage}%
                        </Badge>
                      ) : (
                        <Badge className="bg-green-100 text-green-700">
                          {agent.riskPercentage}%
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="w-full max-w-[120px]">
                        <Progress value={performance} className="h-2" />
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
              {agentStats.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                    No hay datos de agentes
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default TeamsPage;
